import styled from 'styled-components';

import { t } from 'translations/src/LocaleContext';
import { Loader } from 'ui';
import { SmallText } from 'ui/styles/text';

import { useGetMemberDataExports } from '../../hooks/useGetMemberDataExports';
import { useInitMemberDataExport } from '../../hooks/useInitMemberDataExport';
import { ExportsListItem } from './ExportsListItem';

// Wrapper for everything to avoid page jumping when the loader is shown/hidden.
const Container = styled.div`
  margin: 1rem 0;
  min-height: max(20vh, 150px);
`;

const Ul = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: grid;
  gap: 8px;
`;

const StatusText = ({ children }: { children: React.ReactNode }) => (
  <SmallText $textAlign="center" $margin="1.5em 0">
    {children}
  </SmallText>
);

const emptyText = t('membership_hub_data_export_list_empty');

export const ExportsList = () => {
  const { data, isError, isPending } = useGetMemberDataExports();
  const { isPending: isInitPending } = useInitMemberDataExport();

  if (isPending) {
    return (
      <Container>
        <Loader isWholePage={false} />;
      </Container>
    );
  }

  if (isError) {
    return null;
  }

  // Sort exports by createdAt in descending order (most recent first)
  const exports = data?.exports.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()) ?? [];

  if (!exports.length) {
    return (
      <Container>
        <StatusText>{emptyText}</StatusText>
      </Container>
    );
  }

  const hasPendingExport = isInitPending || data?.canInitiateNewExport === false;

  return (
    <>
      {hasPendingExport && (
        <SmallText $margin="-0.25rem 0 0.25rem">{t('membership_hub_data_export_list_description')}</SmallText>
      )}
      <Container>
        <Ul>
          {exports.map((exportRequest) => (
            <ExportsListItem key={exportRequest.id} {...exportRequest} />
          ))}
        </Ul>
      </Container>
    </>
  );
};
